// Where the app goes deep: California, and the Bay Area within it. The one place that says which state, which counties,
// which states have cities and block-level layers, and which states tax a buyer at the purchase price. Logic and lookup
// data read these; prose ("California goes deepest") stays as words. shared/coverage.ts builds its regions from here.
import { ASSUMPTIONS } from "./assumptions.ts";

/** A lon/lat box, west-south then east-north (what MapLibre's fitBounds takes). */
export type Bounds = readonly [readonly [number, number], readonly [number, number]];

export interface State {
  fips: string; // 2-digit state FIPS
  abbr: string; // USPS code, as listings, Zillow and county names ("Marin County, CA") write it
  name: string;
  geoId: string; // geo.geo_id of the state
  /** its ZIPs: inclusive ranges of 5-digit codes, plus border ZIPs counted with it (inState reads a ZIP id by these) */
  zips: { ranges: ReadonlyArray<readonly [string, string]>; also?: readonly string[] };
  /** the map's view of the whole state (web/map.ts VIEWS) */
  bounds: Bounds;
  /** a deep state: what it adds to the core numbers, after "In <name>: " (the welcome card, the home page) */
  depth?: string;
}

/**
 * California. Every ZIP from 90000 to 96199 is California's, and one border ZIP, New Pine Creek 97635, is counted with
 * California by its land.
 */
export const CALIFORNIA = {
  fips: "06", abbr: "CA", name: "California", geoId: "state:06", zips: { ranges: [["90000", "96199"]], also: ["97635"] },
  bounds: [[-124.5, 32.5], [-114.1, 42.0]],
  depth: "every city, what's being built, state housing-law progress, and wildfire and sea-level exposure",
} as const satisfies State;

/**
 * The state the app goes deep in: the map's default view (scope "ca", web/state.ts) shows it with every level and
 * layer, and the footer's Markets link and the map's fallback page open its market page.
 */
export const DEEP_STATE: State = CALIFORNIA;

/** "In California: every city, what's being built, …": what the deep state adds, in one line. */
export const depthLine = (st: State = DEEP_STATE) => (st.depth ? `In ${st.name}: ${st.depth}` : "");

/**
 * Whether an area id lies in a state, from the id alone: states and counties by FIPS, cities by place code (it starts
 * with the state's FIPS), ZIPs by the state's ranges. null = can't tell from the id (metros, the U.S.).
 */
export function inState(st: State, geoId: string): boolean | null {
  const [lvl, code = ""] = geoId.split(":");
  if (lvl === "state" || lvl === "county" || lvl === "city") return code.startsWith(st.fips);
  if (lvl === "zip") return st.zips.ranges.some(([lo, hi]) => code >= lo && code <= hi) || (st.zips.also ?? []).includes(code);
  return null;
}

/** Whether an area id lies in California (inState): the web's scope rules and the server's counts (src/subscribe.ts). */
export const inCalifornia = (geoId: string) => inState(CALIFORNIA, geoId);

/** The nine Bay Area counties (ABAG), north to south-ish: listed first on the scorecard, and "the Bay Area" in rankings. */
export const BAY_AREA: ReadonlyArray<{ fips: string; name: string }> = [
  { fips: "06075", name: "San Francisco" }, { fips: "06081", name: "San Mateo" }, { fips: "06085", name: "Santa Clara" },
  { fips: "06001", name: "Alameda" }, { fips: "06013", name: "Contra Costa" }, { fips: "06041", name: "Marin" },
  { fips: "06097", name: "Sonoma" }, { fips: "06055", name: "Napa" }, { fips: "06095", name: "Solano" },
];
/** The map's view of the Bay Area: where the default view opens (web/map.ts VIEWS). */
export const BAY_AREA_BOUNDS: Bounds = [[-122.95, 36.95], [-121.55, 38.35]];
/** The Bay Area's state. */
export const BAY_AREA_STATE = CALIFORNIA;
/** The `within` token for the Bay Area in rankings and tables (no one geo id covers the nine counties). */
export const BAY_AREA_ID = "bayarea";

/** A region a data point can be limited to (metrics.yaml `coverage:`): by state (FIPS) and, narrower, by county (FIPS). */
export interface Region {
  name: string;
  states: readonly string[];
  counties?: readonly string[];
}

/**
 * The regions below the U.S. that data points exist for (shared/coverage.ts adds "us" and builds COVERAGES from these
 * keys): "ca", the deep state, and "bay_area", its nine counties.
 */
export const REGIONS = {
  ca: { name: CALIFORNIA.name, states: [CALIFORNIA.fips] },
  bay_area: { name: "Bay Area", states: [BAY_AREA_STATE.fips], counties: BAY_AREA.map((c) => c.fips) },
} as const satisfies Record<string, Region>;

/**
 * Cities (incorporated places) and the 2020-block layers (hazards, sea-level rise, SB 79, address zones) exist for these
 * states only (M10: Bay Area first). Adding a state means its block assignment file, its block housing counts and a place
 * boundary file (ingest/sources/catalog.ts); everything else is national already.
 */
export const CITY_STATES: readonly string[] = [CALIFORNIA.fips];

/** Whether a state (FIPS) has cities: the City level exists only in CITY_STATES (not in the U.S. view). */
export const hasCities = (fips: string) => CITY_STATES.includes(fips);

/** Whether a 15-digit census block (or any FIPS code starting with the state) lies in a state with block-level layers. */
export const hasBlockLayers = (block: string) => CITY_STATES.some((f) => block.startsWith(f));

/** The forecast scorecard's state: every county and city in it (src/scorecard.ts). */
export const SCORECARD_STATE = CALIFORNIA.fips;

/**
 * States that reassess a home at its sale price (California's Prop 13): a buyer's property tax is the purchase rate
 * (ASSUMPTIONS.caPurchaseTaxRate), not the area's median bill, which long-time owners' assessments hold down.
 */
export const PURCHASE_RATE_STATES: readonly State[] = [CALIFORNIA];

/** Whether a state (USPS code) taxes a buyer at the purchase price. */
export const reassessesAtPurchase = (abbr: string | null | undefined) => PURCHASE_RATE_STATES.some((s) => s.abbr === abbr);

/** A buyer's property tax rate in a state (USPS code), or null where the area's own rate applies. */
export const purchaseTaxRate = (abbr: string | null | undefined): number | null =>
  reassessesAtPurchase(abbr) ? ASSUMPTIONS.caPurchaseTaxRate : null;

/** The data point whose property tax follows the rule above; anything built on it (metrics.yaml `from:`) does too. */
export const PAYMENT_BASE = "mortgage_payment";

/** Whether a data point's number includes property tax at the purchase-rate rule: it is, or is built on, PAYMENT_BASE. */
export function usesPurchaseRule(id: string, from: (id: string) => readonly string[] | undefined, seen = new Set<string>()): boolean {
  if (id === PAYMENT_BASE) return true;
  if (seen.has(id)) return false;
  seen.add(id);
  return (from(id) ?? []).some((f) => usesPurchaseRule(f, from, seen));
}

/**
 * The property-tax rule in words: for an area in a purchase-rate state (true), elsewhere (false), or a list or map that
 * mixes both (null). The same numbers everywhere; this says what the tax part of them is.
 */
export function taxRuleNote(purchaseRate: boolean | null): string {
  const rate = `${(ASSUMPTIONS.caPurchaseTaxRate * 100).toFixed(2).replace(/0$/, "")}%`;
  const where = PURCHASE_RATE_STATES.map((s) => s.name).join(" and ");
  if (purchaseRate === true) return `Property tax is the ${rate} a buyer pays: ${where} reassesses a home at its purchase price (Prop 13).`;
  if (purchaseRate === false) return "Property tax is what current owners here pay (Census). Where assessments are capped, a new buyer's bill can be higher.";
  return `Property tax: ${where} areas use the ${rate} a buyer pays (reassessed at purchase); other states, what current owners pay, which can understate a new buyer's bill.`;
}

/** `from:` for usesPurchaseRule, from served metric definitions; a personal payment (my_*) is built on PAYMENT_BASE. */
export const fromOf = (get: (id: string) => { from?: readonly string[]; personal?: boolean } | undefined) => (id: string) => {
  const m = get(id);
  return m?.personal ? [PAYMENT_BASE] : m?.from;
};

/**
 * Whether an area (geo id, with its parents' ids) lies in a purchase-rate state; null when it spans or can't tell (U.S.,
 * a metro on its own: pass its `main_state` too, as the build's tax rule goes by it).
 */
export function inPurchaseRateState(ids: readonly string[]): boolean | null {
  const st = ids.map((id) => (id === BAY_AREA_ID ? BAY_AREA_STATE.fips : /^(?:state|county|city):(\d{2})/.exec(id)?.[1])).find(Boolean);
  return st ? PURCHASE_RATE_STATES.some((s) => s.fips === st) : null;
}
